import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormGroup,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Archive as ArchiveIcon, Unarchive as UnarchiveIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Contact, ContactRole, CONTACT_ROLES } from '../../types/contact.types';
import * as contactsApi from '../../api/contacts';
import { getActiveLookupsByType } from '../../api/lookups';
import { Property } from '../../types/property.types';

interface ContactsPanelProps {
  clientId: string;
  properties?: Property[];
}

interface ContactFormState {
  name: string;
  email: string;
  phone: string;
  position: string;
  roles: ContactRole[];
  properties: string[];
}

const emptyForm: ContactFormState = { name: '', email: '', phone: '', position: '', roles: [], properties: [] };

// Audit 07 R-5 — client Contacts manager. Canonical Contact records are the
// operational truth for people; legacy Client.owner*/Property.contact* fields
// remain untouched during the transition.
const ContactsPanel: React.FC<ContactsPanelProps> = ({ clientId, properties = [] }) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [form, setForm] = useState<ContactFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [positions, setPositions] = useState<string[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setContacts(await contactsApi.getClientContacts(clientId, showArchived));
    } catch {
      enqueueSnackbar('Failed to load contacts', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [clientId, showArchived, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getActiveLookupsByType('Positions')
      .then(lookups => setPositions(lookups.map(l => l.value)))
      .catch(() => undefined);
  }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (c: Contact) => {
    setEditing(c);
    setForm({
      name: c.name || '',
      email: c.email || '',
      phone: c.phone || '',
      position: c.position || '',
      roles: c.roles || [],
      properties: (c.properties || []).map(String),
    });
    setDialogOpen(true);
  };

  const toggleRole = (role: ContactRole) =>
    setForm(f => ({
      ...f,
      roles: f.roles.includes(role) ? f.roles.filter(r => r !== role) : [...f.roles, role],
    }));

  const save = async () => {
    if (!form.name.trim()) {
      enqueueSnackbar('Name is required', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await contactsApi.updateContact(editing._id, {
          name: form.name.trim(),
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
          position: form.position.trim() || undefined,
          roles: form.roles,
          properties: form.properties,
        });
      } else {
        await contactsApi.createContact({
          clientId,
          name: form.name.trim(),
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
          position: form.position.trim() || undefined,
          roles: form.roles.length ? form.roles : undefined,
          properties: form.properties,
        });
      }
      setDialogOpen(false);
      await load();
      enqueueSnackbar(editing ? 'Contact updated' : 'Contact added', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.error || 'Failed to save contact', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const archive = async (c: Contact) => {
    try {
      await contactsApi.archiveContact(c._id);
      await load();
      enqueueSnackbar('Contact archived', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.error || 'Failed to archive contact', { variant: 'error' });
    }
  };

  const unarchive = async (c: Contact) => {
    try {
      await contactsApi.unarchiveContact(c._id);
      await load();
      enqueueSnackbar('Contact restored', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.error || 'Failed to restore contact', { variant: 'error' });
    }
  };

  const propertyName = (id: string) =>
    properties.find(p => String(p._id) === String(id))?.name || String(id).slice(-6);

  return (
    <Box sx={{ mt: 2, backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2, p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Contacts</Typography>
        <Box>
          <FormControlLabel
            control={<Checkbox size="small" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />}
            label="Show archived"
          />
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>
            Add Contact
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Position</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Linked properties</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.map((c) => (
                <TableRow key={c._id} sx={{ opacity: c.isActive ? 1 : 0.55 }}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.email || '-'}</TableCell>
                  <TableCell>{c.phone || '-'}</TableCell>
                  <TableCell>{c.position || '-'}</TableCell>
                  <TableCell>
                    {(c.roles || []).map(r => (
                      <Chip key={r} label={r} size="small" sx={{ mr: 0.5, mb: 0.5 }}
                        color={r === 'PRIMARY' ? 'primary' : 'default'} />
                    ))}
                  </TableCell>
                  <TableCell>
                    {(c.properties || []).length
                      ? c.properties.map(p => propertyName(String(p))).join(', ')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip label={c.isActive ? 'Active' : 'Archived'} size="small"
                      color={c.isActive ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    {c.isActive ? (
                      <Tooltip title="Archive">
                        <IconButton size="small" onClick={() => archive(c)}><ArchiveIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Restore">
                        <IconButton size="small" onClick={() => unarchive(c)}><UnarchiveIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {contacts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">
                      No contacts yet — the client's owner details are still used as the default.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="dense" label="Name" required
            value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <TextField fullWidth margin="dense" label="Email" type="email"
            value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          <TextField fullWidth margin="dense" label="Phone"
            value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
          <FormControl fullWidth margin="dense">
            <InputLabel id="contact-position-label">Position</InputLabel>
            <Select
              labelId="contact-position-label"
              value={form.position}
              label="Position"
              onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {Array.from(new Set([...positions, ...(form.position ? [form.position] : [])])).map(p => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Roles</Typography>
          <FormGroup row>
            {CONTACT_ROLES.map(role => (
              <FormControlLabel key={role}
                control={<Checkbox size="small" checked={form.roles.includes(role)} onChange={() => toggleRole(role)} />}
                label={role} />
            ))}
          </FormGroup>

          {properties.length > 0 && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="contact-properties-label">Linked properties (site contact)</InputLabel>
              <Select
                labelId="contact-properties-label"
                multiple
                value={form.properties}
                label="Linked properties (site contact)"
                onChange={(e) => setForm(f => ({ ...f, properties: e.target.value as string[] }))}
                renderValue={(selected) =>
                  (selected as string[]).map(id => propertyName(id)).join(', ')
                }
              >
                {properties.map(p => (
                  <MenuItem key={String(p._id)} value={String(p._id)}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactsPanel;
